/**
 * Phase 1 spine — FundingBar: TCV vs funded, at-a-glance.
 *
 * Projection-aware: the dollar figures (current TCV, total funded) route
 * through <MaskedField> so a Masked/Simplified viewer sees "Restricted"
 * instead of the amounts, while the funded-% bar (a rollup ratio, a Summary
 * signal) still renders — the same boundary KpiSpine draws.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { MONEY_LABELS, MONEY_TIPS } from '../../../constants/deliveryMoney';
import { MaskedField } from '../../../lib/access/MaskedField';
import { programRef } from '../../../lib/access/types';
import { useView } from '../../../lib/access/useView';
import { SourceChip } from '../../../pages/Delivery/_shared/components/SourceChip';
import { formatMoney } from '../../../pages/Delivery/_shared/lib/recordLabels';
import { KpiInfo } from './KpiTile';
import { useProgramFinancialSummary } from './useProgramFinancialSummary';
const fmtDollar = (n)=>`$${n.toLocaleString(undefined, {
        maximumFractionDigits: 0
    })}`;
/**
 * `card` (default) — bordered summary used in the Financial tab.
 * `rail` — bare block matching the program-home briefing rail mockup: a
 * `LABEL … $funded of $tcv` header over a 4px track, plus a faint caption.
 */ export function FundingBar({ programId, variant = 'card', fundedAmount, totalCeiling }) {
    const ref = programRef(programId);
    const view = useView(ref);
    const { data, isLoading, isError } = useProgramFinancialSummary(programId);
    if (view.viewMode === 'no_view') return null;
    const bare = variant === 'rail';
    // The rail renders from the program's own figures when it has them, so only
    // a summary it actually needs can fail it.
    if (isError && !(bare && fundedAmount != null && totalCeiling != null)) {
        // A failed RPC must not read as "No funding data" (0% funded).
        return /*#__PURE__*/ _jsxs("div", {
            className: bare ? '' : 'rounded-lg border bg-card p-4',
            "data-testid": Testids.deliveryFinancial.fundingBar,
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: bare ? 'text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground' : 'text-xs font-medium uppercase tracking-wide text-muted-foreground',
                    children: "Funding"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "mt-2 text-sm text-destructive",
                    children: "Couldn't load funding."
                })
            ]
        });
    }
    // Program Home's rail is the award baseline: funded_amount of total_ceiling.
    // Other FundingBar callers retain the computed funded/TCV summary.
    const tcv = variant === 'rail' && totalCeiling != null ? totalCeiling : data?.currentTcv?.value ?? 0;
    const funded = variant === 'rail' && fundedAmount != null ? fundedAmount : data?.totalFundedAmount?.value ?? 0;
    const pct = variant === 'rail' && totalCeiling != null ? totalCeiling > 0 ? funded / totalCeiling * 100 : 0 : data?.fundedPercentage?.value ?? (tcv > 0 ? funded / tcv * 100 : 0);
    const clamped = Math.max(0, Math.min(100, pct));
    // Where the funded amount is printed (Block 26, or a modification's
    // restated total obligated amount); nothing when no quote is on file.
    const fundedSource = /*#__PURE__*/ _jsx(SourceChip, {
        targetObjectType: "Program",
        targetObjectId: programId,
        fieldPath: "funded_amount",
        resource: ref,
        maskFieldName: "funded_amount"
    });
    if (bare) {
        return /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.deliveryFinancial.fundingBar,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-baseline justify-between gap-2",
                    children: [
                        /*#__PURE__*/ _jsxs("span", {
                            className: "inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                            children: [
                                "Funding",
                                /*#__PURE__*/ _jsx(KpiInfo, {
                                    label: MONEY_LABELS.funded,
                                    tip: MONEY_TIPS.funded,
                                    testid: Testids.deliveryShared.infoTip,
                                    dataName: "fundedAmount"
                                })
                            ]
                        }),
                        tcv <= 0 ? /*#__PURE__*/ _jsxs("span", {
                            className: "text-[11.5px] text-muted-foreground",
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "font-medium tabular-nums text-foreground",
                                    children: [
                                        /*#__PURE__*/ _jsx(MaskedField, {
                                            fieldName: "funded_amount",
                                            resource: ref,
                                            children: formatMoney(funded)
                                        }),
                                        fundedSource
                                    ]
                                }),
                                ' ',
                                "funded · ceiling not stated"
                            ]
                        }) : /*#__PURE__*/ _jsxs("span", {
                            className: "text-[11.5px] text-muted-foreground",
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "font-medium tabular-nums text-foreground",
                                    children: [
                                        /*#__PURE__*/ _jsx(MaskedField, {
                                            fieldName: "funded_amount",
                                            resource: ref,
                                            children: formatMoney(funded)
                                        }),
                                        fundedSource
                                    ]
                                }),
                                ' ',
                                "of",
                                ' ',
                                /*#__PURE__*/ _jsx(MaskedField, {
                                    fieldName: "current_tcv",
                                    resource: ref,
                                    children: formatMoney(tcv)
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "mt-2.5 h-1 w-full overflow-hidden rounded-[2px] bg-muted",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "h-full bg-success transition-[width]",
                        style: {
                            width: `${clamped}%`
                        }
                    })
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "mt-2 text-[11.5px] leading-normal text-muted-foreground",
                    children: "Funding entries appear as modifications are applied."
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-lg border bg-card p-4",
        "data-testid": Testids.deliveryFinancial.fundingBar,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-2 flex items-baseline justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground",
                        children: [
                            "Funding",
                            /*#__PURE__*/ _jsx(KpiInfo, {
                                label: MONEY_LABELS.funded,
                                tip: MONEY_TIPS.funded,
                                testid: Testids.deliveryShared.infoTip,
                                dataName: "fundedAmount"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-xs text-muted-foreground",
                        children: isLoading || tcv === 0 ? '—' : `${clamped.toFixed(0)}% funded`
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "h-2 w-full overflow-hidden rounded-full bg-muted",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "h-full rounded-full bg-success transition-[width]",
                    style: {
                        width: `${clamped}%`
                    }
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-2 flex items-baseline justify-between text-sm",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-semibold tabular-nums",
                        children: tcv === 0 ? /*#__PURE__*/ _jsx("span", {
                            className: "text-muted-foreground",
                            children: "No funding data"
                        }) : /*#__PURE__*/ _jsx(MaskedField, {
                            fieldName: "funded_amount",
                            resource: ref,
                            children: fmtDollar(funded)
                        })
                    }),
                    tcv > 0 && /*#__PURE__*/ _jsxs("span", {
                        className: "text-muted-foreground tabular-nums",
                        children: [
                            "of",
                            ' ',
                            /*#__PURE__*/ _jsx(MaskedField, {
                                fieldName: "current_tcv",
                                resource: ref,
                                children: fmtDollar(tcv)
                            }),
                            ' ',
                            "TCV"
                        ]
                    })
                ]
            })
        ]
    });
}
